import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Check, AlertCircle, Server, Camera as CameraIcon, Download } from 'lucide-react';
import Modal from './Modal';
import Button from './Button';
import Badge from './Badge';
import Loading from './Loading';
import Alert from './Alert';
import { cameraService } from '../../services';
import type { EdgeCameraPreviewResponse, EdgeSyncResponse, EdgeSyncMode } from '../../types';

interface EdgeSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: number;
  locationName: string;
  onSyncComplete?: () => void;
}

const EdgeSyncModal: React.FC<EdgeSyncModalProps> = ({
  isOpen,
  onClose,
  locationId,
  locationName,
  onSyncComplete,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<EdgeCameraPreviewResponse | null>(null);
  const [syncResult, setSyncResult] = useState<EdgeSyncResponse | null>(null);
  const [syncMode, setSyncMode] = useState<EdgeSyncMode>('add_only');
  const [useExternal, setUseExternal] = useState(false);

  const fetchPreview = useCallback(async () => {
    if (!locationId) return;
    try {
      setIsLoading(true);
      setError(null);
      console.log('🔍 Edge Preview - Fetching cameras for location:', locationId);
      const response = await cameraService.previewEdgeCameras(locationId);
      console.log('📡 Edge Preview Response:', response);
      console.log(`📋 Found ${response.total_on_edge} cameras on edge device: ${response.edge_device}`);
      setPreview(response);
    } catch (err) {
      console.error('❌ Edge Preview Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch edge cameras');
    } finally {
      setIsLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    if (isOpen && locationId) {
      fetchPreview();
    } else {
      // Reset state when modal closes
      setPreview(null);
      setSyncResult(null);
      setError(null);
    }
  }, [isOpen, locationId, fetchPreview]);

  const handleSync = async () => {
    try {
      setIsSyncing(true);
      setError(null);
      console.log('🔄 Edge Sync - Starting sync for location:', locationId, 'mode:', syncMode, 'useExternal:', useExternal);
      const response = await cameraService.syncEdgeCameras(locationId, syncMode, useExternal);
      console.log('✅ Edge Sync Response:', response);
      console.log(`📊 Sync Results: Created: ${response.created}, Updated: ${response.updated}, Skipped: ${response.skipped}, Errors: ${response.errors}`);
      setSyncResult(response);
      if (response.success && onSyncComplete) {
        onSyncComplete();
      }
    } catch (err) {
      console.error('❌ Edge Sync Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to sync cameras');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleClose = () => {
    if (!isSyncing) {
      onClose();
    }
  };

  const newCamerasCount = preview?.cameras.filter(c => !c.exists_in_db).length || 0;
  const existingCamerasCount = preview?.cameras.filter(c => c.exists_in_db).length || 0;

  const renderPreview = () => (
    <div className="space-y-4">
      {/* Edge Device Info */}
      <div className="flex items-center gap-3 p-3 bg-surface-secondary rounded-lg">
        <Server size={20} className="text-primary-500" />
        <div>
          <div className="text-sm font-medium text-text-primary">Edge Device</div>
          <div className="text-xs text-text-muted">{preview?.edge_device}</div>
        </div>
        <div className="ml-auto text-right">
          <div className="text-lg font-bold text-text-primary">{preview?.total_on_edge}</div>
          <div className="text-xs text-text-muted">cameras found</div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 bg-status-success/10 rounded-lg border border-status-success/20">
          <div className="flex items-center gap-2">
            <CameraIcon size={16} className="text-status-success" />
            <span className="text-sm font-medium text-status-success">{newCamerasCount} New</span>
          </div>
          <div className="text-xs text-text-muted mt-1">Will be created</div>
        </div>
        <div className="p-3 bg-surface-secondary rounded-lg border border-border-primary">
          <div className="flex items-center gap-2">
            <Check size={16} className="text-text-muted" />
            <span className="text-sm font-medium text-text-muted">{existingCamerasCount} Existing</span>
          </div>
          <div className="text-xs text-text-muted mt-1">Already in database</div>
        </div>
      </div>

      {/* Camera List */}
      <div className="max-h-64 overflow-y-auto border border-border-primary rounded-lg">
        <table className="w-full">
          <thead className="bg-surface-secondary sticky top-0">
            <tr>
              <th className="text-left text-xs font-medium text-text-muted px-3 py-2">Channel</th>
              <th className="text-left text-xs font-medium text-text-muted px-3 py-2">Name</th>
              <th className="text-left text-xs font-medium text-text-muted px-3 py-2">Substream</th>
              <th className="text-left text-xs font-medium text-text-muted px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {preview?.cameras.map((camera) => (
              <tr key={camera.channel} className="border-t border-border-primary">
                <td className="px-3 py-2 text-sm text-text-primary">Ch {camera.channel}</td>
                <td className="px-3 py-2 text-sm text-text-primary">{camera.name}</td>
                <td className="px-3 py-2">
                  {camera.has_substream ? (
                    <Badge variant="info" size="sm">Yes</Badge>
                  ) : (
                    <Badge variant="default" size="sm">No</Badge>
                  )}
                </td>
                <td className="px-3 py-2">
                  {camera.exists_in_db ? (
                    <Badge variant="default" size="sm" dot>Exists</Badge>
                  ) : (
                    <Badge variant="success" size="sm" dot>New</Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sync Options */}
      <div className="space-y-3 p-3 bg-surface-secondary rounded-lg">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">Sync Mode</label>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setSyncMode('add_only')}
              className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                syncMode === 'add_only'
                  ? 'bg-primary-500 border-primary-500 text-white'
                  : 'bg-background-primary border-border-primary text-text-primary hover:border-primary-500'
              }`}
            >
              Add Only
            </button>
            <button
              onClick={() => setSyncMode('add_update')}
              className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                syncMode === 'add_update'
                  ? 'bg-primary-500 border-primary-500 text-white'
                  : 'bg-background-primary border-border-primary text-text-primary hover:border-primary-500'
              }`}
            >
              Add + Update
            </button>
            <button
              onClick={() => setSyncMode('full_sync')}
              className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                syncMode === 'full_sync'
                  ? 'bg-status-warning border-status-warning text-white'
                  : 'bg-background-primary border-border-primary text-text-primary hover:border-status-warning'
              }`}
            >
              Full Sync
            </button>
          </div>
          <p className="text-xs text-text-muted mt-1">
            {syncMode === 'add_only' && 'Only creates cameras that don\'t exist in the database'}
            {syncMode === 'add_update' && 'Creates new cameras and updates names of existing ones'}
            {syncMode === 'full_sync' && 'Creates, updates, and disables cameras not on edge (use with caution)'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="useExternal"
            checked={useExternal}
            onChange={(e) => setUseExternal(e.target.checked)}
            className="w-4 h-4 text-primary-500 border-border-primary rounded focus:ring-primary-500"
          />
          <label htmlFor="useExternal" className="text-sm text-text-primary">
            Use external hostname (DDNS) instead of internal IP
          </label>
        </div>
      </div>
    </div>
  );

  const renderSyncResult = () => (
    <div className="space-y-4">
      {/* Success Banner */}
      {syncResult?.success && (
        <div className="flex items-center gap-3 p-4 bg-status-success/10 rounded-lg border border-status-success/20">
          <div className="w-10 h-10 rounded-full bg-status-success/20 flex items-center justify-center">
            <Check size={20} className="text-status-success" />
          </div>
          <div>
            <div className="font-medium text-status-success">Sync Complete!</div>
            <div className="text-sm text-text-muted">{syncResult.message}</div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-3 bg-status-success/10 rounded-lg text-center">
          <div className="text-2xl font-bold text-status-success">{syncResult?.created || 0}</div>
          <div className="text-xs text-text-muted">Created</div>
        </div>
        <div className="p-3 bg-primary-500/10 rounded-lg text-center">
          <div className="text-2xl font-bold text-primary-500">{syncResult?.updated || 0}</div>
          <div className="text-xs text-text-muted">Updated</div>
        </div>
        <div className="p-3 bg-surface-secondary rounded-lg text-center">
          <div className="text-2xl font-bold text-text-muted">{syncResult?.skipped || 0}</div>
          <div className="text-xs text-text-muted">Skipped</div>
        </div>
        {(syncResult?.disabled || 0) > 0 && (
          <div className="p-3 bg-status-warning/10 rounded-lg text-center">
            <div className="text-2xl font-bold text-status-warning">{syncResult?.disabled || 0}</div>
            <div className="text-xs text-text-muted">Disabled</div>
          </div>
        )}
        {(syncResult?.errors || 0) > 0 && (
          <div className="p-3 bg-status-danger/10 rounded-lg text-center">
            <div className="text-2xl font-bold text-status-danger">{syncResult?.errors || 0}</div>
            <div className="text-xs text-text-muted">Errors</div>
          </div>
        )}
      </div>

      {/* Camera Results List */}
      <div className="max-h-48 overflow-y-auto border border-border-primary rounded-lg">
        <table className="w-full">
          <thead className="bg-surface-secondary sticky top-0">
            <tr>
              <th className="text-left text-xs font-medium text-text-muted px-3 py-2">Channel</th>
              <th className="text-left text-xs font-medium text-text-muted px-3 py-2">Name</th>
              <th className="text-left text-xs font-medium text-text-muted px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {syncResult?.cameras.map((camera) => (
              <tr key={camera.channel} className="border-t border-border-primary">
                <td className="px-3 py-2 text-sm text-text-primary">Ch {camera.channel}</td>
                <td className="px-3 py-2 text-sm text-text-primary">{camera.name}</td>
                <td className="px-3 py-2">
                  {camera.action === 'created' && <Badge variant="success" size="sm">Created</Badge>}
                  {camera.action === 'updated' && <Badge variant="info" size="sm">Updated</Badge>}
                  {camera.action === 'skipped' && <Badge variant="default" size="sm">Skipped</Badge>}
                  {camera.action === 'disabled' && <Badge variant="warning" size="sm">Disabled</Badge>}
                  {camera.action === 'error' && <Badge variant="danger" size="sm">Error</Badge>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const footer = syncResult ? (
    <div className="flex justify-end">
      <Button variant="primary" onClick={handleClose}>
        Done
      </Button>
    </div>
  ) : (
    <div className="flex justify-between">
      <Button
        variant="secondary"
        onClick={fetchPreview}
        leftIcon={<RefreshCw size={16} />}
        disabled={isLoading || isSyncing}
      >
        Refresh
      </Button>
      <div className="flex gap-2">
        <Button variant="ghost" onClick={handleClose} disabled={isSyncing}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSync}
          leftIcon={<Download size={16} />}
          disabled={isLoading || isSyncing || (newCamerasCount === 0 && syncMode === 'add_only')}
          isLoading={isSyncing}
        >
          Sync {newCamerasCount > 0 ? `(${newCamerasCount})` : ''}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Sync Cameras from Edge - ${locationName}`}
      size="lg"
      closeOnOverlayClick={!isSyncing}
      footer={!isLoading && preview && footer}
    >
      {error && (
        <Alert
          variant="error"
          message={error}
          onClose={() => setError(null)}
          className="mb-4"
        />
      )}

      {isLoading && (
        <div className="py-12">
          <Loading size="lg" text="Discovering cameras on edge device..." />
        </div>
      )}

      {!isLoading && !preview && !error && (
        <div className="py-12 text-center">
          <AlertCircle size={48} className="mx-auto text-text-muted mb-4" />
          <p className="text-text-muted">No edge device data available</p>
          <Button variant="primary" onClick={fetchPreview} className="mt-4">
            Retry
          </Button>
        </div>
      )}

      {!isLoading && preview && !syncResult && renderPreview()}
      {syncResult && renderSyncResult()}
    </Modal>
  );
};

export default EdgeSyncModal;
